/* IBM Confidential
 *
 * OCO Source Materials
 *
 * IBM Cognos Products: ba-ui-common
 * (C) Copyright IBM Corp. 2019, 2020
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U.S. Copyright Office.
 * NLS_ENCODING=UTF-8
 * NLS_MESSAGEFORMAT_NONE
 */

import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { omit } from 'lodash';
import { propsUtils } from '@ba-ui-toolkit/utils';
import { Slider as CarbonSlider } from 'carbon';
import styles from './Slider.scss';

const sliderClassName = styles.sliderClassName.replace(/"/g, '');
const carbonContainer = styles.carbonContainer.replace(/"/g, '');
const { BaPropTypes } = propsUtils;

export default class Slider extends Component {
	static propTypes = {
		/**
		 * The CSS class name for the slider.
		 */
		className: PropTypes.string,

		/**
		 * `true` to hide the number input box.
		 */
		hideTextInput: PropTypes.bool,

		/**
		 * The ID of the `<input>`.
		 */
		id: PropTypes.string,

		/**
		 * The callback to get notified of change in value.
		 */
		onChange: PropTypes.func,

		/**
		 * The callback to get notified of value on handle release.
		 */
		onRelease: PropTypes.func,

		/**
		 * The value.
		 */
		value: PropTypes.number.isRequired,

		/**
		 * The minimum value.
		 */
		min: PropTypes.number.isRequired,

		/**
		 * The label associated with the minimum value.
		 */
		minLabel: PropTypes.string,

		/**
		 * The maximum value.
		 */
		max: PropTypes.number.isRequired,

		/**
		 * The label associated with the maximum value.
		 */
		maxLabel: PropTypes.string,

		/**
		 *  legacy toolkit prop, map to formatLabel
		 */
		labelFormatter: PropTypes.func,

		/**
		 * The callback to format the label associated with the minimum/maximum value.
		 */
		formatLabel: PropTypes.func,

		/**
		 * The label for the slider.
		 */
		labelText: PropTypes.node,

		/**
		 * A value determining how much the value should increase/decrease by moving the thumb by mouse.
		 */
		step: PropTypes.number,

		/**
		 * A value determining how much the value should increase/decrease by Shift+arrow keys,
		 * which will be `(max - min) / stepMultiplier`.
		 */
		stepMultiplier: PropTypes.number,

		/**
		 * The child nodes.
		 */
		children: PropTypes.node,

		/**
		 * `true` to disable this slider.
		 */
		disabled: PropTypes.bool,

		/**
		 * The `name` attribute of the `<input>`.
		 */
		name: PropTypes.string,

		/**
		 * The `type` attribute of the `<input>`.
		 */
		inputType: PropTypes.string,

		/**
		 * The `ariaLabel` for the `<input>`.
		 */
		ariaLabelInput: PropTypes.string,

		/**
		 * `true` to use the light version.
		 */
		light: PropTypes.bool,

		/** Toolkit Custom CSS styling */
		style: PropTypes.object,

		// deprecated prop
		// eslint-disable-next-line react/no-unused-prop-types
		showHandleLabel: BaPropTypes.deprecatedByCarbon(CarbonSlider),
		// deprecated prop
		// eslint-disable-next-line react/no-unused-prop-types
		showBoundaryLabels: BaPropTypes.deprecatedByCarbon(CarbonSlider),
		// deprecated prop
		// eslint-disable-next-line react/no-unused-prop-types
		showTooltip: BaPropTypes.deprecatedByCarbon(CarbonSlider),
		// deprecated prop
		// eslint-disable-next-line react/no-unused-prop-types
		size: BaPropTypes.deprecatedByCarbon(CarbonSlider),
	};

	static defaultProps = {
		hideTextInput: true,
		className: '',
	};

	render() {
		const { labelFormatter, formatLabel, onChange, className, ...rest } = this.props;

		const mappedProps = {
			formatLabel: formatLabel || labelFormatter,
		};

		const passThrouProps = omit(rest, [
			'showHandleLabel',
			'showBoundaryLabels',
			'showTooltip',
			'size',
		]);

		return (
			<CarbonSlider
				className={`${className} ${sliderClassName}${carbonContainer}`}
				{...mappedProps}
				{...passThrouProps}
				onChange={({ value }) => onChange(value)}
			/>
		);
	}
}
